(async function(algolia) {
  'use strict';

  const getAlgoliaResults = algolia.externals.getAlgoliaResults;
  const { config, translations, headerTemplate, productTemplate, footerTemplate } = algolia;

  // Filters for stock policy
  let stockPolicyFilter = '';

  if (algolia.config.stock_policy === 'deny') {
    // For 'deny' we will filter out all items based on inventory quantity
    stockPolicyFilter = '(inventory_quantity > 0 OR tags:"cnmc-sold" OR tags:"cnmc-sale-pending")';
  } else if (algolia.config.stock_policy === 'continue') {
    /**
     * For 'continue' we will filter on `inventory_available` attribute whose
     * value is dependent on:
     * `inventory_quantity > 0 OR inventory_policy == 'continue'`
     */
    stockPolicyFilter = 'inventory_available:true';
  }

  const index_suffix = await config.index_suffix;

  algolia.productsPlugin = {
    getSources({ query, setContext }) {
      return [
        {
          sourceId: 'products',
          getItems() {
            return getAlgoliaResults({
              searchClient: algolia.searchClient,
              queries: [
                {
                  indexName: config.index_prefix + 'products' + index_suffix,
                  query,
                  params: {
                    hitsPerPage: config.products_autocomplete_hits_per_page,
                    clickAnalytics: config.analytics_enabled,
                    filters: stockPolicyFilter,
                    distinct: algolia.config.show_products
                  },
                },
              ],
              transformResponse({ results, hits }) {
                setContext({
                  nbProducts: results[0].nbHits,
                });
                return hits;
              },
            });
          },
          templates: {
            header({ html, state }) {
              const resource = translations.products;
              return headerTemplate({ html, state }, resource);
            },
            item({ item, html, components }) {
              const distinct = config.show_products;
              const variantInfo = !distinct && item.objectID !== item.id ? `?variant= ${item.objectID}` : '';
              const itemLink = `${window.Shopify.routes.root}products/${item.handle}/${variantInfo}`;
              const getConversionData = (e, item) => {
                if (!algolia.config.analytics_enabled) return;
                const clickData = {
                  index: item.__autocomplete_indexName,
                  eventName: 'Added to cart',
                  queryID: item.__autocomplete_queryID,
                  objectIDs: [item.objectID],
                };

                algolia.saveForConversionTracking(clickData);
              }
              const addToCartFromAutocomplete = async (event, item) => {
                event.preventDefault();
                event.stopPropagation();

                const variantId = item && item.objectID ? String(item.objectID) : '';
                if (!variantId) {
                  return;
                }

                const button = event.currentTarget;
                if (button?.dataset?.loading === 'true') {
                  return;
                }

                const initialLabel = button ? button.textContent : '';

                try {
                  if (button) {
                    button.dataset.loading = 'true';
                    button.disabled = true;
                    button.setAttribute('aria-busy', 'true');
                    button.textContent = 'Adding...';
                  }

                  const sectionsToBundle = [];
                  document.documentElement.dispatchEvent(
                    new CustomEvent('cart:prepare-bundled-sections', {
                      bubbles: true,
                      detail: { sections: sectionsToBundle },
                    })
                  );

                  const formData = new FormData();
                  formData.set('id', variantId);
                  formData.set('quantity', '1');
                  formData.set('sections', sectionsToBundle.join(','));
                  formData.set(
                    'sections_url',
                    `${window.Shopify.routes.root}products/${item.handle}`
                  );

                  const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
                    body: formData,
                    method: 'POST',
                    headers: {
                      'X-Requested-With': 'XMLHttpRequest',
                    },
                  });

                  const responseJson = await response.json();

                  if (!response.ok) {
                    throw new Error(
                      responseJson?.description || 'Unable to add item to cart.'
                    );
                  }

                  if (algolia.config.analytics_enabled) {
                    const clickData = {
                      index: item.__autocomplete_indexName,
                      eventName: 'Added to cart',
                      queryID: item.__autocomplete_queryID,
                      objectIDs: [item.objectID],
                    };

                    algolia.saveForConversionTracking(clickData);
                  }

                  if (
                    window.themeVariables?.settings?.cartType === 'page' ||
                    window.themeVariables?.settings?.pageType === 'cart'
                  ) {
                    window.location.href = `${window.Shopify.routes.root}cart`;
                    return;
                  }

                  const cartContent = await (
                    await fetch(`${window.Shopify.routes.root}cart.js`)
                  ).json();

                  cartContent.sections = responseJson.sections || cartContent.sections || {};

                  document.documentElement.dispatchEvent(
                    new CustomEvent('cart:change', {
                      bubbles: true,
                      detail: {
                        baseEvent: 'variant:add',
                        onSuccessDo: 'force_open_drawer',
                        cart: cartContent,
                      },
                    })
                  );
                } catch (error) {
                  document.documentElement.dispatchEvent(
                    new CustomEvent('cart:error', {
                      bubbles: true,
                      detail: {
                        error: error?.message || 'Unable to add item to cart.',
                      },
                    })
                  );
                } finally {
                  if (button) {
                    button.dataset.loading = 'false';
                    button.disabled = false;
                    button.removeAttribute('aria-busy');
                    button.textContent = initialLabel;
                  }
                }
              };

              return productTemplate(
                { item, html, components },
                distinct,
                itemLink,
                getConversionData,
                addToCartFromAutocomplete
              );
            },
            footer({ state, html }){
              const {autocomplete_see_all, products_autocomplete_hits_per_page} = config;
              const showFooter = autocomplete_see_all && state.context.nbProducts > products_autocomplete_hits_per_page
              return showFooter && footerTemplate({ html, state });
            },
          },
        }
      ];
    },
  }
})(window.algoliaShopify);
